import type { ErrorInfo } from 'react';
import { withScope, captureException as captureExceptionSentry } from '@sentry/browser';

export type Details = {
	errorInfo?: ErrorInfo;
	teamName?: string;
	packageName?: string;
	hash?: string;
	userLocation?: string;
	[key: string]: string | ErrorInfo | undefined;
};

// structured tags that we send to Sentry
// everything else from details object will go into "extras"
const tags = ['packageName', 'teamName', 'hash', 'userLocation'];
const teamsToFingerprint = ['gryffindor', 'jira-werewolves'];

export const captureException = (location: string, error: Error, details?: Details): void => {
	if (!error) {
		return;
	}
	withScope((scope) => {
		scope.setTag('logger', location);

		// Experimenting with sentry fingerprint for gryffindor team only
		// This is being done to improve the efficiency of sentry error segregation
		const shouldUseFingerprint =
			teamsToFingerprint.includes(details?.teamName ?? '') ||
			(location && location.startsWith('jiraIssueCreate'));

		if (shouldUseFingerprint) {
			const fingerprint = [
				// using {{ default }} as the first value to ensure that we are not
				// completely overriding the fingerprint from Sentry
				// https://docs.sentry.io/platforms/javascript/enriching-events/fingerprinting/#group-errors-with-greater-granularity
				'{{ default }}',
				error.name,
				error.message,
				location,
			].filter((part): part is string => Boolean(part));

			scope.setFingerprint(fingerprint);
		}
		if (details) {
			Object.keys(details).forEach((key) => {
				if (tags.includes(key)) {
					const tagValue = details[key];
					if (typeof tagValue === 'string') {
						scope.setTag(key, tagValue);
					}
				} else {
					scope.setExtra(key, details[key]);
				}
			});
		}
		captureExceptionSentry(error);
	});
};
